import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { API_BASE_URL } from '../../../config/apiConfig';
import {
  fetchCapaEffectivenessReferenceDocument,
  formatCapaEffectivenessReferenceDocumentText,
} from '../utils/fetchCapaEffectivenessReferenceDocument';
import {
  VERIFICATION_METHOD_ITEMS,
  EVIDENCE_REVIEWED_ITEMS,
  EFFECTIVENESS_RESULT_LABELS,
  DECISION_LABELS,
} from '../capaEffectivenessConstants';

/**
 * Printable CAPA Effectiveness Check report (browser print / print page).
 */
const CapaEffectivenessReport = ({ record }) => {
  const [companyLogo, setCompanyLogo] = useState(null);
  const [logoFetchAttempted, setLogoFetchAttempted] = useState(false);
  const [referenceDocument, setReferenceDocument] = useState(null);
  const [referenceDocLoading, setReferenceDocLoading] = useState(true);

  useEffect(() => {
    if (logoFetchAttempted) return;
    const fetchCompanyLogo = async () => {
      setLogoFetchAttempted(true);
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(
          `${API_BASE_URL}/application-setting/key/companyLogo`,
          {
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          },
        );
        if (response.ok) {
          const setting = await response.json();
          if (setting?.value && setting.isActive !== false) {
            let logoUrl = setting.value.trim();
            if (!logoUrl.startsWith('http://') && !logoUrl.startsWith('https://')) {
              const baseUrl = API_BASE_URL.replace('/api', '').replace(/\/$/, '');
              logoUrl = logoUrl.startsWith('/')
                ? `${baseUrl}${logoUrl}`
                : `${baseUrl}/${logoUrl}`;
            }
            setCompanyLogo(logoUrl);
          }
        }
      } catch (error) {
        console.error('Error fetching company logo:', error);
      }
    };
    fetchCompanyLogo();
  }, [logoFetchAttempted]);

  useEffect(() => {
    let cancelled = false;
    const loadReferenceDocument = async () => {
      if (record?.referenceDocument) {
        setReferenceDocument({ snapshotText: record.referenceDocument });
        setReferenceDocLoading(false);
        return;
      }
      setReferenceDocLoading(true);
      try {
        const doc = await fetchCapaEffectivenessReferenceDocument();
        if (!cancelled) setReferenceDocument(doc);
      } catch {
        if (!cancelled) setReferenceDocument(null);
      } finally {
        if (!cancelled) setReferenceDocLoading(false);
      }
    };
    loadReferenceDocument();
    return () => {
      cancelled = true;
    };
  }, [record?.id, record?.referenceDocument]);

  if (!record) {
    return <div>No CAPA effectiveness check data available</div>;
  }

  const formatDateOnly = (date) => {
    if (!date) return 'N/A';
    try {
      return dayjs(date).format('DD/MM/YYYY');
    } catch {
      return date;
    }
  };

  const formatBoolean = (value) => {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return 'N/A';
  };

  const userLabel = (user) =>
    user?.userDisplayName ||
    (user?.userFirstName && user?.userLastName
      ? `${user.userFirstName} ${user.userLastName}`
      : null) ||
    user?.userEmail ||
    'N/A';

  const criteriaRows = [
    ['CAPA actions fully implemented', record.criteriaCapaFullyImplemented],
    ['Form EN.01-ΓΔ.13 completed correctly', record.criteriaEn01Gd13Completed],
    ['Verification date set and met', record.criteriaVerificationDateSet],
    ['No recurrence during monitoring period', record.criteriaNoRecurrence],
    [
      'Staff informed and/or trained on new requirements',
      record.criteriaStaffTrained,
    ],
    ['All related evidence available in QMS', record.criteriaEvidenceInQms],
  ];

  return (
    <div className="capa-effectiveness-report print-only" style={styles.container}>
      <style>{printStyles}</style>

      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.headerLeft}>
            {companyLogo && (
              <img
                src={companyLogo}
                alt="Company Logo"
                style={styles.logo}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
          </div>
          <div style={styles.headerRight}>
            <h1 style={styles.title}>CAPA EFFECTIVENESS CHECK</h1>
            <div style={styles.referenceDocument}>
              {referenceDocLoading ? (
                <span style={styles.referenceDocumentMuted}>Loading…</span>
              ) : (
                referenceDocument?.snapshotText ||
                formatCapaEffectivenessReferenceDocumentText(referenceDocument) || (
                  <span style={styles.referenceDocumentMuted}>N/A</span>
                )
              )}
            </div>
          </div>
        </div>
      </div>

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Basic Information</h2>
        <table style={styles.table}>
          <tbody>
            <tr>
              <td style={styles.labelCell}>CAPA Number:</td>
              <td style={styles.valueCell}>{record.code || 'N/A'}</td>
              <td style={styles.labelCell}>Status:</td>
              <td style={styles.valueCell}>{record.status?.name || 'N/A'}</td>
            </tr>
            <tr>
              <td style={styles.labelCell}>Related Deviation / Finding:</td>
              <td style={styles.valueCell} colSpan={3}>
                {record.relatedDeviationOrFinding || 'N/A'}
              </td>
            </tr>
            <tr>
              <td style={styles.labelCell}>CAPA Description:</td>
              <td style={styles.valueCell} colSpan={3}>
                {record.capaDescription || 'N/A'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Verification Method</h2>
        <p style={styles.intro}>
          Effectiveness of the applied CAPA was assessed through:
        </p>
        <ul style={styles.checkList}>
          {VERIFICATION_METHOD_ITEMS.map((item) => (
            <li key={item.field} style={styles.checkItem}>
              <span style={styles.checkMark}>✓</span>
              {item.label}
            </li>
          ))}
        </ul>
      </div>

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Effectiveness Criteria</h2>
        <table style={styles.table}>
          <tbody>
            {criteriaRows.map(([label, value]) => (
              <tr key={label}>
                <td style={styles.labelCell}>{label}:</td>
                <td style={styles.valueCell}>{formatBoolean(value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Evidence Reviewed</h2>
        <ul style={styles.checkList}>
          {EVIDENCE_REVIEWED_ITEMS.map((item) => (
            <li key={item.field} style={styles.checkItem}>
              <span style={styles.checkMark}>✓</span>
              {item.label}
            </li>
          ))}
        </ul>
        {record.evidenceNotes ? (
          <table style={{ ...styles.table, marginTop: '10px' }}>
            <tbody>
              <tr>
                <td style={styles.labelCell}>Evidence Notes:</td>
                <td style={styles.valueCell}>{record.evidenceNotes}</td>
              </tr>
            </tbody>
          </table>
        ) : null}
      </div>

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Effectiveness Result</h2>
        <table style={styles.table}>
          <tbody>
            <tr>
              <td style={styles.labelCell}>Result:</td>
              <td style={styles.valueCell}>
                {EFFECTIVENESS_RESULT_LABELS[record.effectivenessResult] ||
                  record.effectivenessResult ||
                  'N/A'}
              </td>
              <td style={styles.labelCell}>Decision:</td>
              <td style={styles.valueCell}>
                {DECISION_LABELS[record.decision] || record.decision || 'N/A'}
              </td>
            </tr>
            <tr>
              <td style={styles.labelCell}>Result Notes:</td>
              <td style={styles.valueCell} colSpan={3}>
                {record.effectivenessResultNote || 'N/A'}
              </td>
            </tr>
            <tr>
              <td style={styles.labelCell}>Decision Notes:</td>
              <td style={styles.valueCell} colSpan={3}>
                {record.decisionNote || 'N/A'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Sign-off &amp; Closure</h2>
        <table style={styles.table}>
          <tbody>
            <tr>
              <td style={styles.labelCell}>Check Date:</td>
              <td style={styles.valueCell}>{formatDateOnly(record.checkedAt)}</td>
              <td style={styles.labelCell}>Checked By:</td>
              <td style={styles.valueCell}>{userLabel(record.checkedBy)}</td>
            </tr>
            <tr>
              <td style={styles.labelCell}>Approved Date:</td>
              <td style={styles.valueCell}>{formatDateOnly(record.approvedAt)}</td>
              <td style={styles.labelCell}>Approved By:</td>
              <td style={styles.valueCell}>{userLabel(record.approvedBy)}</td>
            </tr>
            <tr>
              <td style={styles.labelCell}>Closure Date:</td>
              <td style={styles.valueCell}>{formatDateOnly(record.closedAt)}</td>
              <td style={styles.labelCell}>Closed By:</td>
              <td style={styles.valueCell}>{userLabel(record.closedBy)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '210mm',
    margin: '0 auto',
    padding: '20mm',
    backgroundColor: '#fff',
    fontFamily: 'Arial, sans-serif',
    fontSize: '11pt',
    lineHeight: '1.4',
    color: '#000',
  },
  header: {
    borderBottom: '3px solid #000',
    paddingBottom: '10px',
    marginBottom: '20px',
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  headerLeft: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'flex-start',
  },
  headerRight: {
    flex: '1 1 auto',
    textAlign: 'right',
  },
  logo: {
    maxHeight: '50px',
    maxWidth: '200px',
    height: 'auto',
    width: 'auto',
    objectFit: 'contain',
    marginRight: '20px',
  },
  title: {
    fontSize: '22pt',
    fontWeight: 'bold',
    margin: 0,
    color: '#000',
    marginBottom: '5px',
  },
  referenceDocument: {
    fontSize: '11pt',
    margin: 0,
    marginBottom: '5px',
    color: '#000',
    textAlign: 'right',
  },
  referenceDocumentMuted: {
    fontStyle: 'italic',
    color: '#666',
  },
  section: {
    marginBottom: '20px',
    pageBreakInside: 'auto',
  },
  sectionTitle: {
    fontSize: '14pt',
    fontWeight: 'bold',
    marginBottom: '10px',
    paddingBottom: '5px',
    borderBottom: '2px solid #000',
    color: '#000',
  },
  intro: {
    margin: '0 0 8px 0',
    fontSize: '10pt',
  },
  checkList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  checkItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    marginBottom: '6px',
    fontSize: '10pt',
  },
  checkMark: {
    color: '#059669',
    fontWeight: 'bold',
    flexShrink: 0,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '10px',
  },
  labelCell: {
    fontWeight: 'bold',
    padding: '4px 8px 4px 0',
    verticalAlign: 'top',
    width: '22%',
    color: '#333',
  },
  valueCell: {
    padding: '4px 8px 4px 0',
    verticalAlign: 'top',
    width: '28%',
  },
};

const printStyles = `
  @media print {
    @page { margin: 12mm; }
    body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    .no-print { display: none !important; }
    .print-only { display: block !important; }
  }
  @media screen {
    .print-only { display: block; }
  }
`;

export default CapaEffectivenessReport;
